import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Save, RefreshCw, Eye, EyeOff, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { TIMEZONES } from '../../utils/timezoneConstants';

// Legacy numeric-offset list kept only for backward-compat display of old credentials
const UTC_OFFSETS = [
  { value: -12, label: 'UTC-12' }, { value: -11, label: 'UTC-11' },
  { value: -10, label: 'UTC-10 (Hawaii)' }, { value: -9, label: 'UTC-9 (Alaska)' },
  { value: -8, label: 'UTC-8 (Pacific)' }, { value: -7, label: 'UTC-7 (Mountain)' },
  { value: -6, label: 'UTC-6 (Central)' }, { value: -5, label: 'UTC-5 (Eastern)' },
  { value: -4, label: 'UTC-4 (Atlantic)' }, { value: 0, label: 'UTC+0 (London)' },
  { value: 1, label: 'UTC+1' }, { value: 10, label: 'UTC+10 (AEST)' },
  { value: 11, label: 'UTC+11 (AEDT)' },
];

// Map legacy numeric offset to a sensible IANA default (best-effort)
const OFFSET_TO_IANA = {
  '-5': 'America/New_York', '-4': 'America/New_York',
  '-6': 'America/Chicago',  '-7': 'America/Denver',
  '-8': 'America/Los_Angeles', '-9': 'America/Anchorage',
  '-10': 'Pacific/Honolulu',
  '0':  'Europe/London',    '10': 'Australia/Sydney', '11': 'Australia/Sydney',
};

const STATUS_COLORS = {
  success: 'bg-green-100 text-green-700',
  error:   'bg-red-100 text-red-700',
  skipped: 'bg-gray-100 text-gray-600',
};

// ── Company credential panel ──────────────────────────────────────────────────
const CompanyPanel = ({ company, label, available, defaultCreds, onSaved }) => {
  const [creds, setCreds] = useState({ key: '', secret: '', enabled: false, timezone: 'America/New_York' });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const isImpersonating = !!localStorage.getItem('impersonation_id');

  useEffect(() => {
    if (defaultCreds) {
      // Migrate legacy utc_offset to IANA timezone on load
      const migratedCreds = { ...defaultCreds };
      if (!migratedCreds.timezone && migratedCreds.utc_offset !== undefined) {
        migratedCreds.timezone = OFFSET_TO_IANA[String(migratedCreds.utc_offset)] || 'America/New_York';
      }
      setCreds(c => ({ ...c, ...migratedCreds }));
    }
  }, [defaultCreds]);

  const handleSave = async () => {
    if (!creds.key.trim()) { toast.error('API Key is required'); return; }
    setSaving(true);
    try {
      await api.put(`/repository-sync/credentials/${company}`, creds);
      toast.success(`${label} credentials saved`);
      if (onSaved) onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/repository-sync/sync-now/${company}?dry_run=${dryRun}`);
      const d = res.data;
      if (dryRun) {
        toast.success(`Test complete — connection OK. Would create ${d.procedures_would_create ?? 0} procedure(s). No data was saved.`);
      } else {
        toast.success(`Sync complete — ${d.procedures_created} new procedure(s) created across ${d.synced} sale(s)`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Connection test failed'); }
    setSyncing(false);
  };

  if (!available) {
    return (
      <Card className="opacity-60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Lock className="w-4 h-4 text-muted-foreground" /> {label}
            <Badge variant="outline" className="text-xs ml-auto">Coming Soon</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">Integration details pending from {label}.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="text-sm">{label}</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {isImpersonating && (
              <>
                <div className="flex items-center gap-1.5">
                  <Label className="text-xs text-muted-foreground">Dry Run</Label>
                  <Switch checked={dryRun} onCheckedChange={setDryRun} />
                </div>
                <div className="hidden sm:block w-px h-4 bg-border" />
              </>
            )}
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-muted-foreground">Active</Label>
              <Switch
                checked={creds.enabled}
                onCheckedChange={v => setCreds(c => ({ ...c, enabled: v }))}
              />
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
              onClick={handleSyncNow} disabled={syncing || !creds.key}>
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Test Connection
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}
              className="h-8 text-xs bg-primary hover:bg-primary/90 text-white gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Repository Key</Label>
            <Input
              value={creds.key}
              onChange={e => setCreds(c => ({ ...c, key: e.target.value }))}
              placeholder="Provided by Fasig-Tipton"
              className="h-8 text-sm font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Repository Secret</Label>
            <div className="relative">
              <Input
                type={showSecret ? 'text' : 'password'}
                value={creds.secret}
                onChange={e => setCreds(c => ({ ...c, secret: e.target.value }))}
                placeholder={creds._has_secret ? '(saved — enter new to replace)' : 'Provided by Fasig-Tipton'}
                className="h-8 text-sm font-mono pr-9"
              />
              <button
                type="button"
                onClick={() => setShowSecret(s => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Timezone (for active-hours check)</Label>
            <Select value={creds.timezone || 'America/New_York'} onValueChange={v => setCreds(c => ({ ...c, timezone: v }))}>
              <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIMEZONES.map(tz => (
                  <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">IANA timezone — handles Daylight Saving Time automatically</p>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground">
          When active, the system polls Fasig every <strong>10 minutes</strong> during live sales between <strong>5 am – 11 pm</strong> in your timezone.
          New X-ray and scope repository views are automatically created as vetting procedures on the matching sale opportunity.
        </p>

      </CardContent>
    </Card>
  );
};

// ── OBS Panel — per-sale URL links ───────────────────────────────────────────
const OBSPanel = ({ defaultCreds, onSaved }) => {
  const [enabled, setEnabled] = useState(false);
  const [utcOffset, setUtcOffset] = useState(-5);
  const [saleLinks, setSaleLinks] = useState([]); // [{sale_id, url, sale_name}]
  const [activeSales, setActiveSales] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [salesLoading, setSalesLoading] = useState(false);

  // Load active sales for the dropdown
  useEffect(() => {
    setSalesLoading(true);
    api.get('/tenant-sales')
      .then(r => {
        const sales = (r.data || []).filter(s => !s.is_archived);
        setActiveSales(sales);
      })
      .catch(() => {})
      .finally(() => setSalesLoading(false));
  }, []);

  // Apply saved credentials on load
  useEffect(() => {
    if (defaultCreds) {
      setEnabled(!!defaultCreds.enabled);
      setUtcOffset(defaultCreds.utc_offset ?? -5);
      setSaleLinks(defaultCreds.sale_links || []);
    }
  }, [defaultCreds]);

  const addLink = () => setSaleLinks(prev => [...prev, { sale_id: '', url: '', sale_name: '' }]);

  const updateLink = (idx, field, value) => {
    setSaleLinks(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'sale_id') {
        const sale = activeSales.find(s => s.sale_id === value || s.id === value);
        updated[idx].sale_name = sale?.sale_name || '';
        updated[idx].sale_id = sale?.sale_id || sale?.id || value;
      }
      return updated;
    });
  };

  const removeLink = (idx) => setSaleLinks(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    const validLinks = saleLinks.filter(l => l.sale_id && l.url.trim());
    setSaving(true);
    try {
      await api.put('/repository-sync/credentials/obs', {
        enabled,
        utc_offset: utcOffset,
        sale_links: validLinks,
      });
      setSaleLinks(validLinks);
      toast.success('OBS links saved');
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/repository-sync/sync-now/obs');
      const d = res.data;
      toast.success(`Sync complete — ${d.procedures_created} new procedure(s) created across ${d.synced} sale link(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    }
    setSyncing(false);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-sm">OBS (Ocala Breeders' Sales)</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">Active</Label>
                <Switch checked={enabled} onCheckedChange={setEnabled} />
              </div>
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                onClick={handleSyncNow} disabled={syncing || !enabled || saleLinks.length === 0}>
                {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Sync Now
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}
                className="h-8 text-xs bg-primary hover:bg-primary/90 text-white gap-1.5">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timezone */}
        <div className="w-56 space-y-1.5">
          <Label className="text-xs">Timezone (for active-hours check)</Label>
          <Select value={String(utcOffset)} onValueChange={v => setUtcOffset(Number(v))}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {UTC_OFFSETS.map(o => (
                <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sale URL Links */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold">Sale Repository Links</Label>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addLink}>
              <span className="text-base leading-none">+</span> Add Sale Link
            </Button>
          </div>

          {saleLinks.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2 italic">
              No sale links configured yet. Click "Add Sale Link" to connect an OBS repository URL to a sale.
            </p>
          ) : (
            <div className="space-y-2">
              {saleLinks.map((link, idx) => (
                <div key={idx} className="flex flex-col sm:grid sm:grid-cols-[1fr_2fr_auto] gap-2 p-2 border rounded bg-muted/20">
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Sale</Label>
                    <Select
                      value={link.sale_id || ''}
                      onValueChange={v => updateLink(idx, 'sale_id', v)}
                      disabled={salesLoading}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={salesLoading ? 'Loading...' : 'Select sale...'} /></SelectTrigger>
                      <SelectContent>
                        {activeSales.filter(s => s.sale_id || s.id).map(s => (
                          <SelectItem key={s.sale_id || s.id} value={s.sale_id || s.id}>
                            {s.sale_name || s.id}
                          </SelectItem>
                        ))}
                        {activeSales.length === 0 && !salesLoading && (
                          <SelectItem value="__none__" disabled>No active sales found</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-muted-foreground">Repository URL (provided by OBS)</Label>
                    <Input
                      value={link.url}
                      onChange={e => updateLink(idx, 'url', e.target.value)}
                      placeholder="https://..."
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 self-end"
                    onClick={() => removeLink(idx)}>
                    <span className="text-base leading-none">&times;</span>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-[11px] text-muted-foreground">
          OBS provides each consignor with a unique URL per sale for repository access.
          Enter the URL OBS provided for each of your active sales above.
          When active, the system polls each URL every <strong>10 minutes</strong> during live sales between <strong>5 am – 11 pm</strong> in your timezone.
        </p>
      </CardContent>
    </Card>
  );
};

// ── Main tab ──────────────────────────────────────────────────────────────────
const RepositorySyncTab = () => {
  const [allCreds, setAllCreds] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchCreds = async () => {
    setLoading(true);
    try {
      const res = await api.get('/repository-sync/credentials');
      setAllCreds(res.data || {});
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchCreds(); }, []);

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-muted-foreground">
          Connect to sale company repository APIs to automatically import X-ray and scope viewing activity as vetting procedures.
          Syncs run every 10 minutes during live sales between 5 am and 11 pm in your timezone.
        </p>
      </div>

      <CompanyPanel
        company="fasig" label="Fasig-Tipton" available={true}
        defaultCreds={allCreds.fasig} onSaved={fetchCreds}
      />
      <OBSPanel defaultCreds={allCreds.obs} onSaved={fetchCreds} />
      <CompanyPanel company="keeneland" label="Keeneland" available={false} />
      <CompanyPanel company="inglis"    label="Inglis"    available={false} />
    </div>
  );
};

export default RepositorySyncTab;
